/**
 * AddPlayersPanelView — presentation for the add-players surface (SE-5490),
 * re-housed as an overlay drawer for SE-6554 and redesigned to the Scouting
 * drawer spec by SE-6945 (Figma PC1 362:39276).
 *
 * The panel is a RIGHT-ANCHORED 400px OVERLAY, not a left rail. It uses the
 * shared `Drawer` atom — the same overlay ReviewQueueDrawer and the scouting
 * filter drawers use — so the board never reflows when it opens: the drawer
 * paints in the overlay layer and the panel keeps a zero-width footprint in
 * the board row. On a small screen the same drawer anchors to the bottom,
 * matching the repo rule that layout forks on screen size, never device type.
 *
 * Layout order encodes the spec: search and Filters sit ABOVE the tab strip
 * and scope the whole drawer. The strip renders only in the tabbed state —
 * an active search (PC2) or a committed filter set (PC7) hides it and its
 * body replaces the tab body entirely; the in-drawer filters panel (PC3)
 * replaces everything below the header. The footer's ✓ Done closes the
 * drawer in every state except the filters panel, which brings its own
 * Cancel / Show results pair.
 *
 * The drag-to-remove target is deliberately OUTSIDE the drawer. A card is
 * dragged off the board while the drawer is closed, so the removal zone renders
 * as its own absolutely-positioned pad in the board's bottom-left corner and
 * still contributes nothing to layout. The container explains why that pad is
 * compact rather than a full-height strip.
 *
 * The tab bodies own their own ScrollView, so the drawer intentionally does NOT
 * wrap them in `DrawerBody` (which is itself a ScrollView): nesting would put
 * two scrollers on the same axis.
 * @remarks View component — no business logic, hooks, or state.
 */

import { TEST_ID_ADD_PLAYERS_PANEL } from "e2e/testId";
import { Check, Search, X } from "lucide-react-native";
import React, { memo } from "react";

import { Box } from "@/components/atoms/Box";
import { Button } from "@/components/atoms/Button";
import {
  Drawer,
  DrawerBackdrop,
  DrawerContent,
} from "@/components/atoms/Drawer";
import { Input } from "@/components/atoms/Input";
import { Pressable } from "@/components/atoms/Pressable";
import { Stack } from "@/components/atoms/Stack";
import { Text } from "@/components/atoms/Text";
import type {
  PlayerSearchPlayerFragment,
  SuggestionReasonCategory,
} from "@/generated/graphql";

import AddPlayersSourceTabs from "../../molecules/AddPlayersSourceTabs";
import SidebarRemovalZone from "../../molecules/SidebarRemovalZone";
import AddPlayersSearchResults from "../AddPlayersSearchResults";

import type { AddPlayersSource } from "../../../utils/addPlayersPanelSources";

/** Which body owns the drawer below the controls row. */
export type AddPlayersBodyMode = "tabs" | "search" | "filters" | "filtered";

/**
 * Filters-button chrome, resting state (PC1): outline control beside the
 * search field. The design's control row is search + Filters ONLY — the
 * SE-6554 GPR sort select is not in any PC frame and was removed with it.
 */
const FILTERS_BUTTON_CLASS =
  "web:hover:bg-surface-subtle h-9 flex-row items-center justify-center rounded-sm border border-outline-strong bg-surface-base px-4";

/**
 * Filters-button chrome, ACTIVE state (PC3/PC7): while the filters panel owns
 * the drawer, or a committed filter set is applied, the button fills with the
 * accent — the design's way of saying "this control is engaged".
 */
const FILTERS_BUTTON_ACTIVE_CLASS =
  "h-9 flex-row items-center justify-center rounded-sm border border-accent-primary bg-accent-primary px-4";

/**
 * The Filters button's label. On-accent white while the control is engaged
 * (panel open or filters committed, PC3/PC7), secondary otherwise (PC1).
 * @param isActive - Whether the filters control is engaged.
 * @returns The button label.
 */
const renderFiltersLabel = (isActive: boolean): React.ReactElement => (
  <Text
    variant={isActive ? "body-strong" : "body"}
    className={isActive ? "text-content-on-accent" : "text-content-secondary"}
  >
    Filters
  </Text>
);

/**
 * The search field's trailing clear ×, present only while there is text to
 * clear (PC2).
 * @param searchQuery - The current search text.
 * @returns The icon component, or `undefined` for an empty field.
 */
const clearIconFor = (searchQuery: string): typeof X | undefined =>
  searchQuery.length > 0 ? X : undefined;

/** Props for {@link AddPlayersPanelView}. */
interface AddPlayersPanelViewProps {
  /** dnd-kit droppable ref for the drag-to-remove target. */
  readonly setNodeRef: (element: HTMLElement | null) => void;
  /** Class list for the removal-zone overlay, computed by the container. */
  readonly removalZoneClassName: string;
  /** Whether a board card is being dragged, making this the removal target. */
  readonly showRemovalZone: boolean;
  /** Whether the drawer is open. */
  readonly isOpen: boolean;
  /** Close the drawer. */
  readonly onClose: () => void;
  /** Whether the small-screen (bottom-sheet) layout is active. */
  readonly isMobile: boolean;
  /** The pipeline's display name, under the "ADD PLAYERS TO" eyebrow (PC1). */
  readonly boardName: string;
  /** Which body owns the drawer, derived by the container. */
  readonly bodyMode: AddPlayersBodyMode;
  /** Source tabs this pipeline offers. */
  readonly sources: readonly AddPlayersSource[];
  /** Selected source tab. */
  readonly selectedSource: AddPlayersSource;
  /** Per-source select handlers, keyed by source. */
  readonly sourceHandlers: Partial<Record<AddPlayersSource, () => void>>;
  /** Per-source item counts, where the container knows one. */
  readonly tabCounts: Partial<Record<AddPlayersSource, number>>;
  /** Whether any filter is committed, driving the Filters button's fill. */
  readonly isFiltered: boolean;
  /** Open the in-drawer filters panel (PC3). */
  readonly onFiltersOpen: () => void;
  /** Current search text. */
  readonly searchQuery: string;
  /** Update the search text. */
  readonly onSearchChange: (text: string) => void;
  /** Clear the search text, restoring the previous state (PC2). */
  readonly onSearchClear: () => void;
  /** Search results. */
  readonly searchPlayers: readonly PlayerSearchPlayerFragment[];
  /** Fetch the next page of search matches (SE-7100). */
  readonly onSearchLoadMore: () => void;
  /** Whether more search pages exist (SE-7100). */
  readonly searchHasMore: boolean;
  /** Total search match count; `null` while unknown. */
  readonly searchTotalCount: number | null;
  /** Whether search results are loading. */
  readonly isSearchLoading: boolean;
  /** Whether the search matched nothing. */
  readonly isSearchEmpty: boolean;
  /** Search error, or `null`. */
  readonly searchError: string | null;
  /** Players already on this pipeline, for "In list" muting. */
  readonly pipelinePlayerIds: ReadonlySet<string>;
  /** Player ids with an add in flight. */
  readonly addingPlayerIds: readonly string[];
  /** Add a player. */
  readonly onAdd: (
    playerId: string,
    reasons?: readonly SuggestionReasonCategory[]
  ) => void;
  /** The selected tab's body, built by the container. */
  readonly tabBody: React.ReactNode;
  /** The filters panel (PC3), built by the container; `null` unless owning. */
  readonly filterPanelBody: React.ReactNode;
  /** The filtered results (PC7), built by the container; `null` unless owning. */
  readonly filteredResultsBody: React.ReactNode;
}

/**
 * The add-players panel.
 * @param root0 - Component props.
 * @param root0.setNodeRef - Droppable ref.
 * @param root0.removalZoneClassName - Removal-zone overlay classes.
 * @param root0.showRemovalZone - Whether to show the removal target.
 * @param root0.isOpen - Whether the drawer is open.
 * @param root0.onClose - Close callback.
 * @param root0.isMobile - Whether the bottom-sheet layout is active.
 * @param root0.boardName - The pipeline's display name (PC1 header).
 * @param root0.bodyMode - Which body owns the drawer.
 * @param root0.sources - Offered source tabs.
 * @param root0.selectedSource - Selected tab.
 * @param root0.sourceHandlers - Per-source select handlers.
 * @param root0.tabCounts - Per-source item counts.
 * @param root0.isFiltered - Whether any filter is committed.
 * @param root0.onFiltersOpen - Opens the filters panel (PC3).
 * @param root0.searchQuery - Search text.
 * @param root0.onSearchChange - Search change callback.
 * @param root0.onSearchClear - Search clear callback (PC2).
 * @param root0.searchPlayers - Search results.
 * @param root0.onSearchLoadMore - Next-page fetch for the search results (SE-7100).
 * @param root0.searchHasMore - Whether more search pages exist (SE-7100).
 * @param root0.searchTotalCount - Total search match count.
 * @param root0.isSearchLoading - Whether results are loading.
 * @param root0.isSearchEmpty - Whether the search matched nothing.
 * @param root0.searchError - Search error.
 * @param root0.pipelinePlayerIds - Players already on this pipeline.
 * @param root0.addingPlayerIds - Ids with an add in flight.
 * @param root0.onAdd - Add callback.
 * @param root0.tabBody - The selected tab's body.
 * @param root0.filterPanelBody - The filters panel body (PC3).
 * @param root0.filteredResultsBody - The filtered results body (PC7).
 */
const AddPlayersPanelView = ({
  setNodeRef,
  removalZoneClassName,
  showRemovalZone,
  isOpen,
  onClose,
  isMobile,
  boardName,
  bodyMode,
  sources,
  selectedSource,
  sourceHandlers,
  tabCounts,
  isFiltered,
  onFiltersOpen,
  searchQuery,
  onSearchChange,
  onSearchClear,
  searchPlayers,
  onSearchLoadMore,
  searchHasMore,
  searchTotalCount,
  isSearchLoading,
  isSearchEmpty,
  searchError,
  pipelinePlayerIds,
  addingPlayerIds,
  onAdd,
  tabBody,
  filterPanelBody,
  filteredResultsBody,
}: AddPlayersPanelViewProps) => (
  <>
    {showRemovalZone ? (
      <Box ref={setNodeRef} className={removalZoneClassName}>
        <SidebarRemovalZone />
      </Box>
    ) : null}

    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      anchor={isMobile ? "bottom" : "right"}
      size={isMobile ? "lg" : "md"}
    >
      <DrawerBackdrop />
      <DrawerContent
        {...{ "data-testid": TEST_ID_ADD_PLAYERS_PANEL.ROOT }}
        testID={TEST_ID_ADD_PLAYERS_PANEL.ROOT}
        className={isMobile ? "!p-5" : "!w-100 !p-5"}
      >
        {/* PC1 drawer rhythm: 20px padding on all four sides — `p-5` on a
            `w-100` (400px) drawer leaves the design's 360px content column —
            and a 12px gap between the header, the controls row, the tabs and
            the list. The header keeps its extra `pb-4` masthead step from
            SE-6554. */}
        <Stack className="h-full gap-3">
          <Stack direction="horizontal" className="items-center gap-3 pb-4">
            {/* PC1 header: "ADD PLAYERS TO" eyebrow over the pipeline list
                name, so the drawer states its destination — adds are
                immediate, and the user must know where rows land. */}
            <Stack className="flex-1">
              <Text
                variant="micro-strong"
                className="uppercase text-content-muted"
              >
                Add players to
              </Text>
              <Text
                variant="title"
                className="text-content-primary"
                isTruncated
              >
                {boardName}
              </Text>
            </Stack>
            {/* Radius 4 per the artboard, so the close button belongs to the
                same control family as the search field, Filters and GPR below
                it. The Button atom's `shape` enum only offers 8px (`rounded`),
                a pill or a circle, so the 4px rung comes through `className` —
                `rounded-sm` is itself a closed radius token, not an off-scale
                value. */}
            <Button
              testID={TEST_ID_ADD_PLAYERS_PANEL.COLLAPSE_TOGGLE}
              accessibilityLabel="Close add-players panel"
              tone="secondary"
              variant="outline"
              size="sm"
              icon={X}
              iconOnly
              className="rounded-sm"
              onPress={onClose}
            />
          </Stack>

          {/* ── Controls row (PC1) ──
              Search field + Filters on ONE line above the tabs, 36 tall,
              shared outline family. VISIBLE IN EVERY MODE — PC3 keeps the
              row above the filters panel with Filters filled accent, and
              PC7 keeps it above the filtered results. Search and Filters
              scope the WHOLE drawer: an active query (PC2) or a committed
              filter set (PC7) replaces the tab body entirely, whichever
              tab is selected. The PC frames carry NO sort control. */}
          <Stack direction="horizontal" className="items-center gap-2">
            <Box className="flex-1">
              <Input
                size="sm"
                leadingIcon={Search}
                leadingIconTone="muted"
                trailingIcon={clearIconFor(searchQuery)}
                trailingIconTone="muted"
                onTrailingIconPress={onSearchClear}
                trailingIconTestID={TEST_ID_ADD_PLAYERS_PANEL.SEARCH_CLEAR}
                trailingIconAccessibilityLabel="Clear search"
                field={{
                  testID: TEST_ID_ADD_PLAYERS_PANEL.SEARCH_INPUT,
                  accessibilityLabel: "Search all players",
                  placeholder: "Search all players",
                  className: "pl-2",
                  value: searchQuery,
                  onChangeText: onSearchChange,
                }}
              />
            </Box>

            <Pressable
              testID={TEST_ID_ADD_PLAYERS_PANEL.FILTERS_BUTTON}
              accessibilityRole="button"
              accessibilityLabel="Open filters"
              onPress={onFiltersOpen}
              className={
                bodyMode === "filters" || isFiltered
                  ? FILTERS_BUTTON_ACTIVE_CLASS
                  : FILTERS_BUTTON_CLASS
              }
            >
              {renderFiltersLabel(bodyMode === "filters" || isFiltered)}
            </Pressable>
          </Stack>

          {bodyMode === "filters" ? (
            /* PC3: the filters panel replaces everything below the controls
               row — it brings its own FILTERS/CLEAR ALL header row and its
               own Cancel / Show results footer. */
            <Box className="min-h-0 flex-1">{filterPanelBody}</Box>
          ) : (
            <>
              {/* The tab strip renders ONLY in the tabbed state (PC2/PC7 hide
                  it); the selection underneath is untouched, so clearing the
                  query or the last filter restores the exact prior tab. */}
              {bodyMode === "tabs" ? (
                <AddPlayersSourceTabs
                  sources={sources}
                  selected={selectedSource}
                  handlers={sourceHandlers}
                  counts={tabCounts}
                />
              ) : null}

              <Box className="min-h-0 flex-1">
                {bodyMode === "search" ? (
                  <AddPlayersSearchResults
                    players={searchPlayers}
                    onLoadMore={onSearchLoadMore}
                    hasMore={searchHasMore}
                    totalCount={searchTotalCount}
                    isLoading={isSearchLoading}
                    isEmpty={isSearchEmpty}
                    error={searchError}
                    pipelinePlayerIds={pipelinePlayerIds}
                    addingPlayerIds={addingPlayerIds}
                    onAdd={onAdd}
                  />
                ) : bodyMode === "filtered" ? (
                  filteredResultsBody
                ) : (
                  tabBody
                )}
              </Box>

              {/* PC1 footer: ✓ Done closes the drawer. Adds are per-row and
                  immediate, so Done is a dismissal, not a commit — the frame
                  draws it as a full-width OUTLINE button, not a primary. */}
              <Button
                testID={TEST_ID_ADD_PLAYERS_PANEL.DONE_BUTTON}
                accessibilityLabel="Done adding players"
                label="Done"
                icon={Check}
                tone="secondary"
                variant="outline"
                size="sm"
                labelTone="primary"
                className="rounded-sm"
                onPress={onClose}
              />
            </>
          )}
        </Stack>
      </DrawerContent>
    </Drawer>
  </>
);

AddPlayersPanelView.displayName = "AddPlayersPanelView";

export default memo(AddPlayersPanelView);
